import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toAdminUsersApiError } from '../api/adminUsersApi';
import {
  USER_ROLES,
  type AdminUser,
  type CreateUserInput,
  type UpdateUserInput,
  type UserFormValues,
  type UserRole,
} from '../types';

export type UserModalMode = 'create' | 'edit' | 'view';

interface UserFormModalProps {
  open: boolean;
  mode: UserModalMode;
  user: AdminUser | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (input: CreateUserInput | UpdateUserInput) => Promise<void>;
}

type FieldErrors = Partial<Record<keyof UserFormValues, string>>;

const EMPTY_VALUES: UserFormValues = {
  username: '',
  password: '',
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  address: '',
  avatarUrl: '',
  roleType: 'USER',
};

const toValues = (user: AdminUser | null): UserFormValues => user ? {
  username: user.username,
  password: '',
  email: user.email,
  firstName: user.firstName ?? '',
  lastName: user.lastName ?? '',
  phone: user.phone ?? '',
  address: user.address ?? '',
  avatarUrl: user.avatarUrl ?? '',
  roleType: user.role,
} : EMPTY_VALUES;

const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  mode,
  user,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<UserFormValues>(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState('');
  const isReadOnly = mode === 'view';

  useEffect(() => {
    if (!open) return;
    setValues(toValues(user));
    setFieldErrors({});
    setServerError('');
  }, [open, user]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onClose, open]);

  if (!open) return null;

  const setField = (field: keyof UserFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (values.username.trim().length < 3) errors.username = t('admin.users.validation.username');
    if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) errors.email = t('admin.users.validation.email');
    if (mode === 'create' && values.password.length < 8) errors.password = t('admin.users.validation.password');
    if (values.phone && !/^(\+\d{1,3}[- ]?)?\d{9,15}$/.test(values.phone.trim())) {
      errors.phone = t('admin.users.validation.phone');
    }
    return errors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isReadOnly) return;

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }

    const commonInput: UpdateUserInput = {
      username: values.username.trim(),
      email: values.email.trim(),
      firstName: values.firstName?.trim() || undefined,
      lastName: values.lastName?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      address: values.address?.trim() || undefined,
      avatarUrl: values.avatarUrl?.trim() || undefined,
      roleType: values.roleType,
    };

    setServerError('');
    try {
      await onSubmit(mode === 'create'
        ? { ...commonInput, password: values.password }
        : commonInput);
      onClose();
    } catch (error: unknown) {
      const normalizedError = toAdminUsersApiError(error);
      setServerError(normalizedError.message);
      setFieldErrors(Object.fromEntries(
        Object.entries(normalizedError.fieldErrors ?? {}).map(([field, messages]) => [field, messages[0]]),
      ) as FieldErrors);
    }
  };

  const inputClass = 'h-11 w-full rounded-xl border border-outline-variant bg-white px-3 text-body-md text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15 disabled:bg-surface-container-low disabled:text-secondary';
  const title = mode === 'create'
    ? t('admin.users.modal.create_title')
    : mode === 'edit'
      ? t('admin.users.modal.edit_title')
      : t('admin.users.modal.view_title');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-3 sm:p-5"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="flex max-h-[92vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[20px] border border-outline-variant bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-modal-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-outline-variant px-4 py-4 sm:px-6">
          <div>
            <h2 id="user-modal-title" className="font-h2 text-h2 text-on-surface">{title}</h2>
            <p className="mt-1 text-body-sm text-secondary">{t('admin.users.modal.description')}</p>
          </div>
          <button
            type="button"
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-secondary hover:bg-surface-container-low disabled:opacity-50"
            disabled={isSubmitting}
            aria-label={t('admin.users.actions.close')}
            onClick={onClose}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <form id="user-account-form" className="overflow-y-auto px-4 py-4 sm:px-6" onSubmit={handleSubmit}>
          {serverError && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-error/30 bg-error-container/40 px-3 py-2 text-body-sm text-error" role="alert">
              <span className="material-symbols-outlined text-[19px]">error</span>
              <span>{serverError}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 font-label-md text-on-surface sm:col-span-2">
              {t('admin.users.fields.username')} <span className="text-error">*</span>
              <input
                className={inputClass}
                value={values.username}
                disabled={isReadOnly || isSubmitting}
                autoComplete="off"
                onChange={(event) => setField('username', event.target.value)}
              />
              {fieldErrors.username && <span className="text-body-sm font-normal text-error">{fieldErrors.username}</span>}
            </label>

            {mode === 'create' && (
              <label className="flex flex-col gap-1.5 font-label-md text-on-surface sm:col-span-2">
                {t('admin.users.fields.password')} <span className="text-error">*</span>
                <input
                  type="password"
                  className={inputClass}
                  value={values.password}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  onChange={(event) => setField('password', event.target.value)}
                />
                {fieldErrors.password && <span className="text-body-sm font-normal text-error">{fieldErrors.password}</span>}
              </label>
            )}

            <label className="flex flex-col gap-1.5 font-label-md text-on-surface sm:col-span-2">
              {t('admin.users.fields.email')} <span className="text-error">*</span>
              <input
                type="email"
                className={inputClass}
                value={values.email}
                disabled={isReadOnly || isSubmitting}
                onChange={(event) => setField('email', event.target.value)}
              />
              {fieldErrors.email && <span className="text-body-sm font-normal text-error">{fieldErrors.email}</span>}
            </label>

            <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
              {t('admin.users.fields.first_name')}
              <input className={inputClass} value={values.firstName} disabled={isReadOnly || isSubmitting} onChange={(event) => setField('firstName', event.target.value)} />
              {fieldErrors.firstName && <span className="text-body-sm font-normal text-error">{fieldErrors.firstName}</span>}
            </label>
            <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
              {t('admin.users.fields.last_name')}
              <input className={inputClass} value={values.lastName} disabled={isReadOnly || isSubmitting} onChange={(event) => setField('lastName', event.target.value)} />
              {fieldErrors.lastName && <span className="text-body-sm font-normal text-error">{fieldErrors.lastName}</span>}
            </label>

            <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
              {t('admin.users.fields.phone')}
              <input className={inputClass} value={values.phone} disabled={isReadOnly || isSubmitting} onChange={(event) => setField('phone', event.target.value)} />
              {fieldErrors.phone && <span className="text-body-sm font-normal text-error">{fieldErrors.phone}</span>}
            </label>
            <label className="flex flex-col gap-1.5 font-label-md text-on-surface">
              {t('admin.users.fields.role')} <span className="text-error">*</span>
              <select
                className={inputClass}
                value={values.roleType}
                disabled={isReadOnly || isSubmitting}
                onChange={(event) => setField('roleType', event.target.value as UserRole)}
              >
                {USER_ROLES.map((role) => (
                  <option key={role} value={role}>{t(`admin.users.roles.${role.toLowerCase()}`)}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5 font-label-md text-on-surface sm:col-span-2">
              {t('admin.users.fields.address')}
              <input className={inputClass} value={values.address} disabled={isReadOnly || isSubmitting} onChange={(event) => setField('address', event.target.value)} />
              {fieldErrors.address && <span className="text-body-sm font-normal text-error">{fieldErrors.address}</span>}
            </label>
            <label className="flex flex-col gap-1.5 font-label-md text-on-surface sm:col-span-2">
              {t('admin.users.fields.avatar_url')}
              <input type="url" className={inputClass} value={values.avatarUrl} disabled={isReadOnly || isSubmitting} onChange={(event) => setField('avatarUrl', event.target.value)} />
              {fieldErrors.avatarUrl && <span className="text-body-sm font-normal text-error">{fieldErrors.avatarUrl}</span>}
            </label>
          </div>
        </form>

        <footer className="flex flex-col-reverse gap-2 border-t border-outline-variant bg-surface-container-lowest px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low disabled:opacity-50"
            disabled={isSubmitting}
            onClick={onClose}
          >
            {isReadOnly ? t('admin.users.actions.close') : t('admin.users.actions.cancel')}
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              form="user-account-form"
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 font-label-md text-on-primary hover:bg-on-primary-fixed-variant disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting && <span className="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>}
              {isSubmitting ? t('admin.users.actions.saving') : t(mode === 'create' ? 'admin.users.actions.create' : 'admin.users.actions.save')}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
};

export default UserFormModal;
